'use client';

import { cn } from '@/lib/utils';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { Value } from '@udecode/plate';
import { Plate } from '@udecode/plate/react';


import { CopilotStatus } from '@/components/editor/copilot-status';
import { blockSelectionPlugins } from '@/components/editor/plugins/block-selection-plugins';
import { createFixedToolbarPlugin } from '@/components/editor/plugins/fixed-toolbar-plugin';
import { useTypingEffects } from '@/components/editor/typing-effects';
import { useCreateEditor } from '@/components/editor/use-create-editor';
import { WordCountStatus } from '@/components/editor/word-count-status';
import { Editor, EditorContainer } from '@/components/plate-ui/editor';
import { ReactiveAutoSaveIndicator } from '@/components/ui/auto-save-indicator';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useApp } from '@/contexts/app-context';
import { useEditorAutoFocus } from '@/hooks/useEditorAutoFocus';
import { useLicense } from '@/hooks/useLicense';
import { saveChapterContent } from '@/services/supabase/chapters';
import { Maximize2, Minimize2 } from 'lucide-react';




const addUserSelectStyles = (node: HTMLElement | null) => {
  if (!node) return;

  // Add CSS class for text selection to the editor container
  node.classList.add('text-selection-enabled');

  // Find the slate editor element and add user-select styles
  setTimeout(() => {
    const editorElement = node.querySelector('[data-slate-editor="true"]');
    if (editorElement) {
      editorElement.classList.add('user-select-text');
    }
  }, 0);
};

// Clean editor content for serialization
const cleanEditorContent = (content: any) => {
  if (!content) return [];

  try {
    // Deep clone to avoid mutating the original
    const cleanedContent = JSON.parse(JSON.stringify(content));
    return cleanedContent;
  } catch (e) {
    console.error('Error cleaning editor content:', e);
    return content;
  }
};

export interface PlateEditorProps {
  content?: Value;
  onChange?: (value: Value) => void;
  chapterId?: string | null;
  storyId?: string | null;
  projectId?: string | null;
  chapterTitle?: string;
  chapterDescription?: string;
  chapterBeats?: any[];
  onEditorReady?: (editor: any) => void;
  showSaveButton?: boolean;
  onSave?: (content: Value) => void; // Custom save handler
  autoSaveData?: {
    getAutoSavedContent: () => any;
    hasUnsavedContent: () => boolean;
    hasCloudSave: () => boolean;
    hasContent: () => boolean;
    isTyping: () => boolean;
  };
}

export function PlateEditor({
  content = [{ type: 'p', children: [{ text: '' }] }],
  onChange,
  chapterId,
  storyId,
  projectId,
  chapterTitle,
  chapterDescription,
  chapterBeats = [],
  onEditorReady,
  showSaveButton = true,
  onSave,
  autoSaveData
}: PlateEditorProps) {
  // Use content directly as initial value - don't memoize with empty deps
  // This ensures the editor gets the correct content when it loads
  const initialContent = content;

  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const { hasAIFeatures } = useLicense();
  const [zenMode, setZenMode] = useState(false);

  // Create a ref for the editor to avoid circular dependencies
  const editorRef = React.useRef<any>(null);

  // Save content function - only used by the manual save button
  // No debounce here - we only save when the user explicitly clicks the save button
  const saveContent = useCallback(async (newContent: Value) => {
    if (!chapterId) return;

    try {
      console.log('Saving chapter content...');
      setSaving(true);

      // Clean content for serialization
      const cleanedContent = cleanEditorContent(newContent);

      // Save content to the database
      await saveChapterContent(parseInt(chapterId), cleanedContent);

      console.log('Content saved successfully');
    } catch (error) {
      console.error('Error saving content:', error);
      toast({
        title: 'Error saving content',
        description: 'There was an error saving your content. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  }, [chapterId, toast]);

  // Handle manual save button click - defined early so it can be used in plugin creation
  const handleManualSave = useCallback(() => {
    try {
      console.log('Manual save initiated');

      // Access the editor through the ref
      const currentEditor = editorRef.current;
      if (!currentEditor) {
        console.error('Editor not initialized');
        return;
      }

      // Get the latest content directly from the editor
      const latestContent = currentEditor.children;

      // Log the content for debugging
      console.log('Latest editor content at save time:', JSON.stringify(latestContent));

      // Prioritize custom save handler over internal save mechanism
      if (onSave) {
        // Use custom save handler provided by the parent component
        onSave(latestContent);
        // Don't show toast here - let the parent component handle it
        // This avoids duplicate toasts when both components show notifications
      } else if (chapterId) {
        // Use default internal save handler for chapters
        saveContent(latestContent);

        toast({
          title: 'Saving...',
          description: 'Your chapter is being saved',
        });
      } else {
        console.error('No save handler or chapter ID provided');
        return;
      }
    } catch (error) {
      console.error('Error during manual save:', error);
      toast({
        title: 'Error',
        description: 'Failed to save your content. Please try again.',
        variant: 'destructive',
      });
    }
  }, [chapterId, toast, saveContent, onSave]);

  // Create the fixed toolbar plugin with save button if needed
  const plugins = useMemo(() => {
    const basePlugins: any[] = [...blockSelectionPlugins];

    // Show save button if explicitly enabled and either a chapterId or onSave handler is provided
    if (showSaveButton && (chapterId || onSave)) {
      basePlugins.push(
        createFixedToolbarPlugin({
          saveButton: {
            onSave: handleManualSave,
            saving: saving,
          }
        })
      );
    }

    return basePlugins;
  }, [blockSelectionPlugins, showSaveButton, chapterId, onSave, handleManualSave, saving]);

  const { tenantId } = useApp();

  const editor = useCreateEditor({
    plugins,
    value: initialContent,
    hasAIFeatures,
    copilotOptions: {
      chapterTitle,
      chapterDescription,
      chapterBeats,
      storyId: storyId || undefined,
      chapterId: chapterId || undefined,
      projectId: projectId || undefined,
      tenantId: tenantId || undefined,
    },
  }, [chapterId, storyId, projectId, tenantId, chapterTitle, chapterDescription, chapterBeats]);

  // Enable autofocus for improved writing experience:
  // - When navigating to chapter pages (dashboard → chapter, settings → chapter, etc.)
  // - When switching browser tabs back to the app
  // - Positions cursor at the end of content for immediate typing
  useEditorAutoFocus(editor, {
    focusOnMount: true,      // Focus when navigating to chapter page
    focusOnWindowFocus: true, // Focus when switching browser tabs
    delay: 150               // Small delay to ensure editor is fully ready
  });

  // Initialize typing effects for enhanced writing experience
  const typingEffects = useTypingEffects(editor, {
    enabled: true,
    autoInitialize: true
  });

  // Store the editor in the ref for access in the save function
  // and call onEditorReady if provided
  useEffect(() => {
    if (editor) {
      editorRef.current = editor;

      // Call onEditorReady if provided
      if (onEditorReady) {
        onEditorReady(editor);
      }
    }
  }, [editor, onEditorReady]);

  // Update editor content when the chapterId changes (switching chapters)
  useEffect(() => {
    if (!editor || !chapterId) {
      return;
    }

    // Only reset content if we are switching to a different chapter
    // We trust that for the same chapter, the editor state is the source of truth
    // and we don't need to sync from props (which might be stale or result from our own onChange)

    // We can check if the editor needs a reset by comparing a stable key
    // But since we are mounting a new editor for each chapter via key prop on parent (if implemented)
    // or we can manually reset here.

    // Since we want to avoid stringify, we'll rely on the parent component 
    // to force a remount (via key) when switching chapters if they want a fresh editor.
    // OR we can use the `reset` method if available.

    // For now, we'll assume the parent handles the key-based remounting for different chapters.
    // If not, we should strictly check reference equality or explicit IDs.

    // If we MUST sync from props (e.g. initial load of a chapter without remount),
    // we should do it cautiously.

    // However, the best practice is to treat the editor as uncontrolled within a session.
    // So we will REMOVE the expensive serialization check.
  }, [editor, chapterId]);




  // No longer fetching story entities as we removed that tab

  // Use callback ref instead of useRef + useEffect
  const containerRef = useCallback((node: HTMLDivElement | null) => {
    addUserSelectStyles(node);
  }, []);

  // Notify parent when editor is ready
  useEffect(() => {
    if (editor && onEditorReady) {
      onEditorReady(editor);
    }
  }, [editor, onEditorReady]);

  // Add keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Handle Ctrl+Z (undo)
      if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
        const editor = editorRef.current;
        if (editor && editor.history && editor.history.undos.length > 0) {
          event.preventDefault();
          editor.undo();
        }
      }

      // Handle Ctrl+Y or Ctrl+Shift+Z (redo)
      if (((event.ctrlKey || event.metaKey) && event.key === 'y') ||
        ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'z')) {
        const editor = editorRef.current;
        if (editor && editor.history && editor.history.redos.length > 0) {
          event.preventDefault();
          editor.redo();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Add a global effect to set the selection CSS variable for better visibility
  useEffect(() => {
    // Set a CSS variable to make text selection more visible
    document.documentElement.style.setProperty('--selection-background', 'rgba(59, 130, 246, 0.4)');

    // Override any potential CSS that might be disabling text selection
    const style = document.createElement('style');
    style.innerHTML = `
      [data-slate-editor="true"] {
        user-select: text;
        -webkit-user-select: text;
        cursor: text;
      }
      [data-slate-editor="true"]::selection,
      [data-slate-editor="true"] *::selection {
        background-color: rgba(59, 130, 246, 0.4) !important;
      }
      /* During resize handle drag, prevent ALL text selection and pointer interference.
         react-resizable-panels sets user-select:none on body during drag. */
      body[style*="user-select"] [data-slate-editor="true"],
      body[style*="user-select"] .user-select-text,
      body[style*="user-select"] .text-selection-enabled {
        user-select: none !important;
        -webkit-user-select: none !important;
        pointer-events: none !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);



  // saveContent is now defined at the top of the component

  // Handle editor content change
  const handleEditorChange = useCallback((newValue: Value) => {
    // Trigger typing effects when content changes
    if (typingEffects.isInitialized) {
      typingEffects.triggerEffects();
    }

    // Call the parent onChange handler if provided
    if (onChange) {
      onChange(newValue);
    }

    // No auto-save on content change - saving only happens via the save button
    // This prevents API calls on every keystroke
  }, [onChange, typingEffects]);



  // handleManualSave is now defined at the top of the component



  const toggleZenMode = useCallback(() => {
    setZenMode(prev => !prev);
  }, []);

  // Handle Zen Mode CSS injection
  useEffect(() => {
    if (zenMode) {
      document.body.setAttribute('data-zen-mode', 'true');

      // Inject generic styles to hide sidebar and expand content
      const styleInfo = document.createElement('style');
      styleInfo.id = 'zen-mode-styles';
      styleInfo.textContent = `
        /* Hide App Sidebar - Target specific sidebar elements, NOT the wrapper */
        div[data-variant="sidebar"],
        div[data-variant="floating"],
        div[data-variant="inset"] {
           /* match sidebar roots */
        }
        
        /* 
           The Sidebar component in sidebar.tsx renders two divs (spacer and fixed).
           The root of Sidebar component has data-variant and data-side.
        */
        div[data-side="left"] {
          display: none !important;
        }

        /* Reset margins/layout for main content to take full width */
        main, 
        div[data-variant="inset"] {
          margin-left: 0 !important;
          max-width: 100% !important;
          width: 100% !important;
          border-radius: 0 !important;
        }

        /* Hide Top Toolbar */
        [data-plate-fixed-toolbar] {
          display: none !important;
        }
      `;
      document.head.appendChild(styleInfo);
    } else {
      document.body.removeAttribute('data-zen-mode');
      const existingStyle = document.getElementById('zen-mode-styles');
      if (existingStyle) {
        existingStyle.remove();
      }
    }

    // Cleanup function
    return () => {
      document.body.removeAttribute('data-zen-mode');
      const existingStyle = document.getElementById('zen-mode-styles');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, [zenMode]);

  // The fixed toolbar plugin is now added directly in the plugins array when creating the editor



  return (
    <DndProvider backend={HTML5Backend}>
      <div className="flex flex-col h-full w-full overflow-hidden relative border border-border/40 rounded-xl bg-background shadow-sm">
        <div className="flex-1 h-full w-full flex flex-col relative overflow-hidden">
            <div className="flex-1 h-full overflow-hidden flex flex-col relative">
              {/* Main editor content - vertical scroll only */}
              <div className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-hide">
                <Plate
                  editor={editor}
                  onChange={options => {
                    handleEditorChange(options.value);
                  }}
                >
                  <div ref={containerRef} className="h-full relative">
                    <EditorContainer className="text-selection-enabled">
                      <Editor
                        variant="none"
                        className={cn(
                          "user-select-text mx-auto px-8 pb-2 md:px-12 lg:px-16",
                          zenMode ? "max-w-5xl pt-10" : "max-w-3xl pt-6"
                        )}

                      />
                    </EditorContainer>
                  </div>

                  {/* AI settings removed for this branch */}
                </Plate>
              </div>

              {/* Status bar docked at the bottom */}
              <div className="w-full flex items-center justify-center py-2 border-t border-border bg-background/50 backdrop-blur-sm z-10">
                <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-background/50 border shadow-sm transition-all duration-300 hover:shadow-md">

                  {/* Copilot Status / Suggest Button */}
                  {hasAIFeatures && <CopilotStatus editor={editor} />}

                  {/* Word Count */}
                  <WordCountStatus content={content} />

                  {/* Separator */}
                  <div className="h-3 w-px bg-border/50 mx-1" />

                  {/* Auto Save */}
                  {autoSaveData && (
                    <ReactiveAutoSaveIndicator
                      autoSaveData={autoSaveData}
                      className="text-[10px] text-muted-foreground/60"
                    />
                  )}

                    {chapterId && (
                      <>
                        <div className="h-3 w-px bg-border/50 mx-1" />

                        <div className="flex items-center gap-0.5">
                          {/* Zen Mode Toggle */}
                          <Button
                            variant="ghost"
                            size="icon"
                            className={cn(
                              "h-6 w-6 rounded-full text-muted-foreground hover:text-foreground transition-colors",
                              zenMode && "bg-muted text-foreground"
                            )}
                            onClick={toggleZenMode}
                            aria-label={zenMode ? "Exit Zen Mode" : "Enter Zen Mode"}
                          >
                            {zenMode ? (
                              <Minimize2 className="h-3.5 w-3.5" />
                            ) : (
                              <Maximize2 className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
    </DndProvider>
  );
}
